import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, Space, Typography, message, Popconfirm, Row, Col, Checkbox,
} from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../api/client';

const { Title } = Typography;
const { Option } = Select;

interface Role {
  id: number;
  name: string;
  description: string;
}

const ROLE_COLORS: Record<string, string> = {
  admin: 'red',
  accountant: 'blue',
  info: 'green',
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Администратор',
  accountant: 'Бухгалтерия',
  info: 'Информационная',
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data.items);
    } catch {
      message.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // Fetch roles from the first user's data or hardcode
    // Since we don't have a roles endpoint, we'll use known roles
    setRoles([
      { id: 1, name: 'admin', description: 'Администратор' },
      { id: 2, name: 'accountant', description: 'Бухгалтерия' },
      { id: 3, name: 'info', description: 'Информационная' },
    ]);
  }, []);

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (user: any) => {
    setEditingUser(user);
    form.resetFields();
    form.setFieldsValue({
      username: user.username,
      display_name: user.display_name,
      role_id: user.role_id,
      usb_scan_listener: user.workplace_features?.usb_scan_listener === true,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        const payload: any = {
          display_name: values.display_name,
          role_id: values.role_id,
        };
        if (values.password) {
          payload.password = values.password;
        }
        if (values.onec_username !== undefined && values.onec_username !== '') {
          payload.onec_username = values.onec_username;
          payload.onec_password = values.onec_password || '';
        } else if (values.onec_clear) {
          payload.onec_username = '';
        }
        if (values.pin) {
          payload.pin = values.pin;
        } else if (values.pin_clear) {
          payload.pin = '';
        }
        await api.put(`/users/${editingUser.id}`, payload);
        // Профиль функций места (workplace_auth §4) — merge по ключам
        if (editingUser.is_workplace) {
          await api.put(`/users/${editingUser.id}/workplace-features`, {
            features: { usb_scan_listener: !!values.usb_scan_listener },
          });
        }
        message.success('Пользователь обновлён');
      } else {
        await api.post('/users', values);
        message.success('Пользователь создан');
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err: any) {
      if (err.response?.data?.detail) {
        message.error(err.response.data.detail);
      }
    }
  };

  const handleDeactivate = async (userId: number) => {
    try {
      await api.delete(`/users/${userId}`);
      message.success('Пользователь деактивирован');
      fetchUsers();
    } catch (err: any) {
      message.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const columns = [
    {
      title: 'Логин',
      dataIndex: 'username',
      key: 'username',
      render: (v: string, rec: any) => (
        <Space size={4}>
          {v}
          {rec.is_workplace && <Tag color="blue">место</Tag>}
        </Space>
      ),
    },
    {
      title: 'Имя',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (v: string) => v || '—',
    },
    {
      title: 'Роль',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (v: string) => (
        <Tag color={ROLE_COLORS[v] || 'default'}>
          {ROLE_LABELS[v] || v}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'red'}>{v ? 'Активен' : 'Деактивирован'}</Tag>
      ),
    },
    {
      title: '1С',
      dataIndex: 'onec_configured',
      key: 'onec_configured',
      render: (v: boolean) => (
        v ? <Tag color="green">настроен</Tag> : <Tag>—</Tag>
      ),
    },
    {
      title: 'PIN',
      dataIndex: 'has_pin',
      key: 'has_pin',
      render: (v: boolean) => (
        v ? <Tag color="green">задан</Tag> : <Tag>—</Tag>
      ),
    },
    {
      title: 'Последний вход',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (v: string) => v ? new Date(v).toLocaleString('ru-RU') : '—',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, rec: any) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(rec)} />
          {rec.is_active && (
            <Popconfirm
              title="Деактивировать пользователя?"
              onConfirm={() => handleDeactivate(rec.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button type="link" size="small" danger icon={<StopOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Пользователи</Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchUsers}>Обновить</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Добавить
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
      />

      <Modal
        title={editingUser ? 'Редактирование пользователя' : 'Новый пользователь'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="Логин"
            rules={[{ required: !editingUser, message: 'Обязательное поле' }]}
          >
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="display_name" label="Отображаемое имя">
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label={editingUser ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль'}
            rules={editingUser ? [] : [{ required: true, message: 'Обязательное поле' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="role_id"
            label="Роль"
            rules={[{ required: !editingUser, message: 'Выберите роль' }]}
          >
            <Select placeholder="Выберите роль">
              {roles.map((r) => (
                <Option key={r.id} value={r.id}>{r.description}</Option>
              ))}
            </Select>
          </Form.Item>
          {editingUser && (
            <>
              <Form.Item
                name="onec_username"
                label="Имя пользователя 1С (оставьте пустым, чтобы не менять)"
              >
                <Input autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="onec_password"
                label="Пароль 1С (хранится в зашифрованном виде)"
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              {editingUser.onec_configured && (
                <Form.Item name="onec_clear" valuePropName="checked">
                  <Checkbox>Очистить 1С-данные пользователя</Checkbox>
                </Form.Item>
              )}
              <Form.Item
                name="pin"
                label="PIN для рабочего места (4 цифры; пусто — не менять)"
                rules={[
                  { pattern: /^\d{4}$/, message: 'Ровно 4 цифры' },
                ]}
              >
                <Input.Password maxLength={4} autoComplete="new-password" style={{ maxWidth: 160 }} />
              </Form.Item>
              {editingUser.has_pin && (
                <Form.Item name="pin_clear" valuePropName="checked">
                  <Checkbox>Сбросить PIN</Checkbox>
                </Form.Item>
              )}
              {editingUser.is_workplace && (
                <Form.Item
                  name="usb_scan_listener"
                  valuePropName="checked"
                  tooltip="Глобальный слушатель USB-сканера на всех страницах (marking_buffer §6)"
                >
                  <Checkbox>USB-сканер: принимать сканы на любой странице</Checkbox>
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
